import type { ChangeEvent, FocusEvent, MouseEvent, PointerEvent, ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { cn } from '@crossnokaye/ux-common';
import { Input } from '@crossnokaye/ux-common/ckds';

import { resolveSourceUnits } from '@/features/equipment-browser/utils';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';

interface EquipmentBulkEditTableInputCellProps {
  columnId: string;
  rowId: string;
  displayUnits?: string;
  units?: string;
  disabled?: boolean;
}

/**
 * Cell that displays a setting value (metrics, temperature, etc.).
 * Binds to React Hook Form context using the path `values.{rowId}.{columnId}`.
 * Converts from source units to display units for display, and converts
 * back from display units to source units on change.
 *
 * Uses local state during editing to allow intermediate values like trailing
 * decimal points (e.g. "1.") that would otherwise be stripped by formatUnits.
 * The form value is updated with the converted source units on blur.
 */
export const EquipmentBulkEditTableInputCell = ({
  columnId,
  rowId,
  displayUnits,
  units,
  disabled,
}: EquipmentBulkEditTableInputCellProps): ReactNode => {
  const { control, getFieldState } = useFormContext();
  const fieldName = `values.${rowId}.${columnId}`;
  const { isDirty } = getFieldState(fieldName);
  const { field } = useController({
    control,
    name: fieldName,
  });

  const sourceUnits = resolveSourceUnits(displayUnits, units);

  // Track whether the input is focused so we can show raw typed text
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // For editable inputs, empty/null values should show an empty input (not "--")
  const isValueEmpty = field.value == null || field.value === '';
  const displayValue = isValueEmpty
    ? { valueLabel: '', unitsLabel: '' }
    : formatUnits({ value: field.value, units: sourceUnits }, displayUnits);

  const handleFocus = useCallback(() => {
    if (disabled) {
      inputRef.current?.blur();
      return;
    }
    setIsEditing(true);
    setLocalValue(displayValue.valueLabel);
  }, [disabled, displayValue.valueLabel]);

  // While editing, store the raw typed value locally without conversion
  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  // On blur, convert from display units to source units and persist to the form
  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setIsEditing(false);
      const rawValue = e.target.value;
      if (rawValue === '') {
        field.onChange('');
      } else {
        field.onChange(
          formatUnits({ value: rawValue, units: displayUnits }, sourceUnits).valueLabel
        );
      }
      field.onBlur();
    },
    [displayUnits, sourceUnits, field]
  );

  const blockCellClickEvent = useCallback(
    (e: MouseEvent<HTMLInputElement> | PointerEvent<HTMLInputElement>) => {
      if (!disabled) {
        return;
      }
      // Prevent focus and bubbling when interaction is blocked for this cell.
      e.preventDefault();
      e.stopPropagation();
    },
    [disabled]
  );

  return (
    <Input
      ref={inputRef}
      className={cn(
        'text-foreground text-sm border-none rounded-none',
        isDirty && 'text-ckds-base-caution',
        disabled && 'cursor-not-allowed'
      )}
      readOnly={disabled}
      tabIndex={disabled ? -1 : 0}
      inlineLabel={
        <span className={cn(isDirty && 'text-ckds-base-caution')}>{displayValue.unitsLabel}</span>
      }
      onPointerDownCapture={blockCellClickEvent}
      onClickCapture={blockCellClickEvent}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      value={isEditing ? localValue : displayValue.valueLabel}
    />
  );
};
